<template>
  <div>
    <popup-hint hint="products">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Products">
          <template v-slot:left v-if="inBondLowInventoryCount+dutyPaidLowInventoryCount">
            <list-notification v-model="showStatus">
              <v-list-item v-if="inBondLowInventoryCount">
                <v-icon>mdi-arrow-right-thin</v-icon>
                  {{inBondLowInventoryCount}} product{{inBondLowInventoryCount>1?'s':''}} with low in-bond inventory
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.categoryId=null;filters.siteId=null;filters.status='1';filters.mixedPack='',filters.strength='0';filters.inBondLowInventory='1';filters.dutyPaidLowInventory='0';filters.asof=today})">View</v-btn>
              </v-list-item>
              <v-list-item v-if="dutyPaidLowInventoryCount">
                <v-icon>mdi-arrow-right-thin</v-icon>
                  {{dutyPaidLowInventoryCount}} product{{dutyPaidLowInventoryCount>1?'s':''}} with low duty-paid inventory
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.categoryId=null;filters.siteId=null;filters.status='1';filters.mixedPack='',filters.strength='0';filters.inBondLowInventory='0';filters.dutyPaidLowInventory='1';filters.asof=today})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        Bottled products are either in-bond (held under your Excise Warehouse License)
        or duty-paid, where they have been moved from bond and excise is payable.
      </template>
      <template v-slot:bottom>
        You can create products individually or do a batch import of products.
      </template>
      <template v-slot:actions="{close}">
        <v-btn color="primary" class="ml-4" small @click="close();$refs.import.doImport()">Import</v-btn>
        <v-btn @click="close()" :to="{name:'InventoryProductsEditNew'}" color="primary" class="ml-4" small>Create</v-btn>
      </template>
    </popup-hint>

    <inventory-products-import v-model="doImport" ref="import" @refresh="$refs.table ? $refs.table.buildList() : ''"/>

    <purtrak-table name="products" objectType="products" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="products2" v-model="selected" @list="listBuilt"
                   fields="id,name,active,alcohol{type,name},sku,bondedLowInventory,dutyPaidLowInventory,duration,
                            category{id,name},mixedPack,containerType
                            description
                            pictureURL,caseSize,bottleSize,containerType,strength,palletHI,palletTI,bottlesPerUnit,
                            inBondBottles,dutyPaidBottles,inBondValue,dutyPaidValue,
                            inBondOnOrder,dutyPaidOnOrder,
                            sites{siteId,lot,strength,inBondBottles,inBondValue,dutyPaidBottles,dutyPaidValue},
                            onSalesOrder{inBondProduct,bottlesOnOrder}
                            caseWeight,upc,scc,defaultBondedPrice,defaultPrice"
                   totalFields="totalBottlesInBondOnHand,totalCasesInBondOnHand,totalUnitsInBondOnHand,totalPalletsInBondOnHand,totalLAAInBondOnHand,totalLitresInBondOnHand,
                                totalBottlesDutyPaidOnHand,totalCasesDutyPaidOnHand,totalUnitsDutyPaidOnHand,totalPalletsDutyPaidOnHand,totalLAADutyPaidOnHand,totalLitresDutyPaidOnHand,
                                totalBottlesInBondOnOrder,totalCasesInBondOnOrder,totalUnitsInBondOnOrder,totalPalletsInBondOnOrder,totalLAAInBondOnOrder,totalLitresInBondOnOrder,
                                totalBottlesDutyPaidOnOrder,totalCasesDutyPaidOnOrder,totalUnitsDutyPaidOnOrder,totalPalletsDutyPaidOnOrder,totalLAADutyPaidOnOrder,totalLitresDutyPaidOnOrder,
                                totalValueInBond,totalValueDutyPaid"
                   :totals.sync="totals"
                   :listFilters="listFilters" :parameters="listParameters"
                   @dblclick="product => $router.push({name:'InventoryProductsTransactions',params:{productId:product.id}})"
                   :item-class="product => (product.active ? '' : 'grey--text')"
                   title="Products"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[`As of ${$formatDate(filters.asof)}`,
                                          filters.inBondLowInventory=='1'?'In-bond low inventory only':'',
                                          filters.dutyPaidLowInventory=='1'?'Duty-paid low inventory only':'',
                                          filters.categoryId!=null?categoryName:'',
                                          filters.status!='1'?(filters.status=='0'?'Inactive':'All'):'',
                                          filters.strength!='0'?(filters.strength=='1'?'<= 7%':'> 7%'):'',
                                          filters.siteId!=null?siteName:'']}"
                   download-endpoint="productsExportCSV">
      <template v-slot:filter>
        <scan-code @code="scan" return-unmatched-code auto-close/>
        <filter-menu include-reset @reset="filters.categoryId=null;filters.siteId=null;filters.status='1';filters.mixedPack='',filters.strength='0';filters.inBondLowInventory='0';filters.dutyPaidLowInventory='0';filters.asof=today;filters.search='';filters.id=null;filters.upc=''">
          <v-list-item>
            <filter-search :label="[{text:'Name',value:'name'},{text:'SKU',value:'sku'},{text:'Lot',value:'lot'}]" v-model="filters.search" :search-field.sync="filters.searchField" :search-display.sync="searchDisplay"/>
          </v-list-item>
          <v-list-item>
            <v-checkbox v-model="filters.inBondLowInventory" true-value="1" false-value="0" label="In-bond low inventory only" class="mt-n4"/>
          </v-list-item>
          <v-list-item>
            <v-checkbox v-model="filters.dutyPaidLowInventory" true-value="1" false-value="0" label="Duty-paid low inventory only" class="mt-n4"/>
          </v-list-item>
          <v-list-item>
            <inventory-categories-select v-model="filters.categoryId" label="Category" type="Product" :name.sync="categoryName" :category-list.sync="categories" none-text="All"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Mixed-pack" dense outlined :items="[{value:'',text:'All'},{divider:true},{value:'0',text:'No mixed packs'},{value:'1',text:'Only mixed packs'}]" v-model="filters.mixedPack"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'Active'},{value:'0',text:'Inactive'}]" v-model="filters.status"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Strength" dense outlined :items="[{value:'0',text:'All'},{divider:true},{value:'1',text:'<= 7%'},{value:'2',text:'> 7%'}]" v-model="filters.strength"/>
          </v-list-item>
          <v-list-item v-if="allSites.length>1">
            <v-autocomplete label="Site" dense outlined :items="[{id:null,name:'All'},{divider:true},...allSites]" v-model="filters.siteId" item-text="name" item-value="id"/>
          </v-list-item>
          <v-list-item>
            <date-picker label="As of" v-model="filters.asof" date-only/>
          </v-list-item>
        </filter-menu>
        <v-select :items="['bottles','cases','units','pallets','LAA','litres']" v-model="units" dense hide-details class="text-caption" style="max-width:75px"/>

        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.id||filters.upc" close small color="primary" class="ml-1" @click:close="filters.id=null;filters.upc=''">Scanned product</v-chip>
          <v-chip v-show="filters.asof!==today" close small class="ml-1" @click:close="filters.asof=today">As of {{filters.asof | formatDate}}</v-chip>
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">{{searchDisplay}}</v-chip>
          <v-chip v-show="filters.inBondLowInventory=='1'" close small class="ml-1" @click:close="filters.inBondLowInventory='0'">In-bond low inventory only</v-chip>
          <v-chip v-show="filters.dutyPaidLowInventory=='1'" close small class="ml-1" @click:close="filters.dutyPaidLowInventory='0'">Duty-paid low inventory only</v-chip>
          <v-chip v-show="filters.categoryId!=null" close small class="ml-1" @click:close="filters.categoryId=null">{{categoryName}}</v-chip>
          <v-chip v-show="filters.strength!='0'" close small class="ml-1" @click:close="filters.strength='0'">{{filters.strength=='1'?'<= 7%':'> 7%'}}</v-chip>
          <v-chip v-show="filters.mixedPack!=''" close small class="ml-1" @click:close="filters.mixedPack=''">{{filters.mixedPack=='0'?'No mixed packs':'Only mixed packs'}}</v-chip>
          <v-chip v-show="filters.status!='1'" close small class="ml-1" @click:close="filters.status='1'">{{filters.status=='0'?'Inactive':'All'}}</v-chip>
          <v-chip v-show="filters.siteId!=null" close small class="ml-1" @click:close="filters.siteId=null">{{siteName}}</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'InventoryProductsEditNew'}"><v-list-item-title>Create product</v-list-item-title></v-list-item>
          <v-divider/>
        </template>
        <v-list-item :to="{name:'InventoryProductCategories'}"><v-list-item-title>Categories</v-list-item-title></v-list-item>
        <v-menu :nudge-left="220" v-if="!$vuetify.breakpoint.mobile">
          <template v-slot:activator="{on:reports}">
            <v-list-item v-on="reports">
              <v-list-item-title>Reports</v-list-item-title>
              <v-list-item-action><v-icon>mdi-chevron-right</v-icon></v-list-item-action>
            </v-list-item>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Products',to:{name:'InventoryProducts'}}]},query:{to:'reports/inventory/ReportsProductsActivity'}}"><v-list-item-title>Products activity</v-list-item-title></v-list-item>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Products',to:{name:'InventoryProducts'}}]},query:{to:'reports/inventory/ReportsProductsPeriodSummary'}}"><v-list-item-title>Products period summary</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
        <v-divider/>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-list-item @click="doImport=true"><v-list-item-title>Import products from CSV</v-list-item-title></v-list-item>
          <template v-if="$store.getters.company.connectedToAccounting">
            <v-divider/>
            <v-list-item @click="sync"><v-list-item-title>Sync products with QBO</v-list-item-title></v-list-item>
          </template>
        </template>
        <v-divider/>
        <v-list-item :to="{name:'InventoryProductsBatchMove'}"><v-list-item-title>Batch product move</v-list-item-title></v-list-item>
        <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Products',to:{name:'InventoryProducts'}}]},query:{to:'reports/inventory/ReportsInventoryProductsPhysical'}}"><v-list-item-title>Physical inventory worksheet</v-list-item-title></v-list-item>
        <v-list-item :to="{name:'InventoryProductsReconcile'}"><v-list-item-title>Reconcile product inventory</v-list-item-title></v-list-item>
      </template>

      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <inventory-products-batch-edit :products="selected" @refresh="$refs.table ? $refs.table.buildList($event) : ''"/>
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'InventoryProductsEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.category.name="{item}">
        {{item.category ? item.category.name : ''}}
      </template>
      <template v-slot:item.inBondBottles="{item,printing}">
        <template v-if="printing">
          {{convertToUnits(item, item.inBondBottles) | number(precision)}} {{displayUnits(item)}}
        </template>
        <span v-else :class="{'warning--text':item.bondedLowInventory}">
          {{convertToUnits(item, item.inBondBottles) | number(precision)}} {{displayUnits(item)}}
        </span>
        <div v-if="item.bondedLowInventory && !printing">
          <v-btn v-if="item.mixedPack" rounded color="warning lighten-1" x-small :to="{name:'ProductionPackingEditNew',params:{productId:item.id}}">Pack</v-btn>
          <v-btn v-else rounded color="warning lighten-1" x-small :to="{name:'ProductionBottlingEditNew',params:{productId:item.id}}">Bottle</v-btn>
        </div>
      </template>
      <template v-slot:item.dutyPaidBottles="{item,printing}">
        <template v-if="printing">
          {{convertToUnits(item, item.dutyPaidBottles) | number(precision)}} {{displayUnits(item)}}
        </template>
        <span v-else :class="{'warning--text':item.dutyPaidLowInventory}">
          {{convertToUnits(item, item.dutyPaidBottles) | number(precision)}} {{displayUnits(item)}}
        </span>
        <div v-if="item.dutyPaidLowInventory && !printing">
          <v-btn v-if="item.mixedPack" rounded color="warning lighten-1" x-small :to="{name:'ProductionPackingEditNew',params:{productId:item.id}}">Pack</v-btn>
          <v-btn v-else-if="item.alcohol.type==='NonExcise'" rounded color="warning lighten-1" x-small :to="{name:'ProductionBottlingEditNew',params:{productId:item.id}}">Bottle</v-btn>
          <v-btn v-else rounded color="warning lighten-1" x-small :to="{name:'InventoryProductsMoveToDutyPaid',params:{productId:item.id}}">Move from in-bond</v-btn>
        </div>
      </template>
      <template v-slot:item.inBondOnOrder="{item}">
        {{convertToUnits(item, item.inBondOnOrder) | number(precision)}} {{displayUnits(item)}}
      </template>
      <template v-slot:item.dutyPaidOnOrder="{item}">
        {{convertToUnits(item, item.dutyPaidOnOrder) | number(precision)}} {{displayUnits(item)}}
      </template>
      <template v-slot:item.inBondValue="{value}">
        {{value | currency}}
      </template>
      <template v-slot:item.dutyPaidValue="{value}">
        {{value | currency}}
      </template>
      <template v-slot:item.inBondAvailable="{item,printing}">
        <template v-if="printing">
          {{convertToUnits(item, item.inBondBottles - item.inBondOnOrder) | number(precision)}} {{displayUnits(item)}}
        </template>
        <template v-else>
          <v-tooltip bottom :disabled="!item.inBondOnOrder">
            <template v-slot:activator="{on}">
              <div :class="{'warning--text':item.bondedLowInventory}" v-on="on" class="d-inline">
                {{convertToUnits(item, item.inBondBottles - item.inBondOnOrder) | number(precision)}} {{displayUnits(item)}}
              </div>
            </template>
            {{convertToUnits(item, item.inBondBottles) | number(precision)}} {{displayUnits(item)}} on hand -
            {{convertToUnits(item, item.inBondOnOrder) | number(precision)}} {{displayUnits(item)}} on order
          </v-tooltip>
          <template v-if="item.bondedLowInventory">
            <v-btn v-if="item.mixedPack" rounded color="warning lighten-1" x-small :to="{name:'ProductionPackingEditNew',params:{productId:item.id}}">Pack</v-btn>
            <v-btn v-else rounded color="warning lighten-1" x-small :to="{name:'ProductionBottlingEditNew',params:{productId:item.id}}">Bottle</v-btn>
          </template>
        </template>
      </template>
      <template v-slot:item.dutyPaidAvailable="{item,printing}">
        <template v-if="printing">
          {{convertToUnits(item, item.dutyPaidBottles - item.dutyPaidOnOrder) | number(precision)}} {{displayUnits(item)}}
        </template>
        <template v-else>
          <v-tooltip bottom :disabled="!item.dutyPaidOnOrder">
            <template v-slot:activator="{on}">
              <div :class="{'warning--text':item.dutyPaidLowInventory}" v-on="on" class="d-inline">
                {{convertToUnits(item, item.dutyPaidBottles - item.dutyPaidOnOrder) | number(precision)}} {{displayUnits(item)}}
              </div>
            </template>
            {{convertToUnits(item, item.dutyPaidBottles) | number(precision)}} {{displayUnits(item)}} on hand -
            {{convertToUnits(item, item.dutyPaidOnOrder) | number(precision)}} {{displayUnits(item)}} on order
          </v-tooltip>
          <div v-if="item.dutyPaidLowInventory">
            <v-btn v-if="item.mixedPack" rounded color="warning lighten-1" x-small :to="{name:'ProductionPackingEditNew',params:{productId:item.id}}">Pack</v-btn>
            <v-btn v-else-if="item.alcohol.type==='NonExcise'" rounded color="warning lighten-1" x-small :to="{name:'ProductionBottlingEditNew',params:{productId:item.id}}">Bottle</v-btn>
            <v-btn v-else rounded color="warning lighten-1" x-small :to="{name:'InventoryProductsMoveToDutyPaid',params:{productId:item.id}}">Move from in-bond</v-btn>
          </div>
        </template>
      </template>
      <template v-slot:item.name="{item,printing}">
        <template v-if="printing">{{item.name}}</template>
        <template v-else>
          <v-hover v-if="!readOnly" v-slot="{hover}">
            <a @click.stop="$router.push({name:'InventoryProductsEdit',params:{productId:item.id}})"
               :class="{'text-decoration-underline':hover}">{{item.name}}</a>
          </v-hover>
          <template v-else>{{item.name}}</template>
          <div v-if="!item.active" class="grey--text text-caption">Inactive</div>
        </template>
      </template>
      <template v-slot:item.duration="{value}">
        {{value | duration}}
      </template>
      <template v-slot:item.defaultBondedPrice="{value}">
        {{value | currency}}
      </template>
      <template v-slot:item.defaultPrice="{value}">
        {{value | currency}}
      </template>
      <template v-slot:item.bottleSize="{value}">
        {{value}}ml
      </template>
      <template v-slot:item.strength="{value}">
        {{value}}% alc./vol.
      </template>
      <template v-slot:item.inBondPerCase="{item}">
        {{item.inBondBottles ? item.inBondValue/item.inBondBottles*item.caseSize : 0 | currency}}
      </template>
      <template v-slot:item.inBondPerBottle="{item}">
        {{item.inBondBottles ? item.inBondValue/item.inBondBottles : 0 | currency}}
      </template>
      <template v-slot:item.dutyPaidPerCase="{item}">
        {{item.dutyPaidBottles ? item.dutyPaidValue/item.dutyPaidBottles*item.caseSize : 0 | currency}}
      </template>
      <template v-slot:item.dutyPaidPerBottle="{item}">
        {{item.dutyPaidBottles ? item.dutyPaidValue/item.dutyPaidBottles : 0 | currency}}
      </template>
      <template v-slot:item.action="{item}">
        <inventory-product-menu :product="item" :readOnly="readOnly" :siteId="filters.siteId"
                              @delete="productToDelete=item;showDelete=true"/>
      </template>
      <template v-slot:body.append="{headers,printing}" v-if="!$vuetify.breakpoint.mobile">
        <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
          <template v-for="header of headers">
            <td class="text-right" v-if="header.value==='inBondBottles'">
              {{totals[{bottles:'totalBottlesInBondOnHand',cases:'totalCasesInBondOnHand',units:'totalUnitsInBondOnHand',
                        pallets:'totalPalletsInBondOnHand',LAA:'totalLAAInBondOnHand',litres:'totalLitresInBondOnHand'}[units]] | number(precision)}} {{units === 'bottles' ? '' : units}}
            </td>
            <td class="text-right" v-else-if="header.value==='inBondOnOrder'">
              {{totals[{bottles:'totalBottlesInBondOnOrder',cases:'totalCasesInBondOnOrder',units:'totalUnitsInBondOnOrder',
                        pallets:'totalPalletsInBondOnOrder',LAA:'totalLAAInBondOnOrder',litres:'totalLitresInBondOnOrder'}[units]] | number(precision)}} {{units === 'bottles' ? '' : units}}
            </td>
            <td class="text-right" v-else-if="header.value==='inBondAvailable'">
              {{totals[{bottles:'totalBottlesInBondOnHand',cases:'totalCasesInBondOnHand',units:'totalUnitsInBondOnHand',
                        pallets:'totalPalletsInBondOnHand',LAA:'totalLAAInBondOnHand',litres:'totalLitresInBondOnHand'}[units]] -
                totals[{bottles:'totalBottlesInBondOnOrder',cases:'totalCasesInBondOnOrder',units:'totalUnitsInBondOnOrder',
                        pallets:'totalPalletsInBondOnOrder',LAA:'totalLAAInBondOnOrder',litres:'totalLitresInBondOnOrder'}[units]] | number(precision)}} {{units === 'bottles' ? '' : units}}
            </td>
            <td class="text-right" v-else-if="header.value==='inBondValue'">
              {{totals.totalValueInBond | currency}}
            </td>
            <td class="text-right" v-else-if="header.value==='dutyPaidBottles'">
              {{totals[{bottles:'totalBottlesDutyPaidOnHand',cases:'totalCasesDutyPaidOnHand',units:'totalUnitsDutyPaidOnHand',
                        pallets:'totalPalletsDutyPaidOnHand',LAA:'totalLAADutyPaidOnHand',litres:'totalLitresDutyPaidOnHand'}[units]] | number(precision)}} {{units === 'bottles' ? '' : units}}
            </td>
            <td class="text-right" v-else-if="header.value==='dutyPaidOnOrder'">
              {{totals[{bottles:'totalBottlesDutyPaidOnOrder',cases:'totalCasesDutyPaidOnOrder',units:'totalUnitsDutyPaidOnOrder',
                        pallets:'totalPalletsDutyPaidOnOrder',LAA:'totalLAADutyPaidOnOrder',litres:'totalLitresDutyPaidOnOrder'}[units]] | number(precision)}} {{units === 'bottles' ? '' : units}}
            </td>
            <td class="text-right" v-else-if="header.value==='dutyPaidAvailable'">
              {{totals[{bottles:'totalBottlesDutyPaidOnHand',cases:'totalCasesDutyPaidOnHand',units:'totalUnitsDutyPaidOnHand',
                        pallets:'totalPalletsDutyPaidOnHand',LAA:'totalLAADutyPaidOnHand',litres:'totalLitresDutyPaidOnHand'}[units]] -
                totals[{bottles:'totalBottlesDutyPaidOnOrder',cases:'totalCasesDutyPaidOnOrder',units:'totalUnitsDutyPaidOnOrder',
                        pallets:'totalPalletsDutyPaidOnOrder',LAA:'totalLAADutyPaidOnOrder',litres:'totalLitresDutyPaidOnOrder'}[units]] | number(precision)}} {{units === 'bottles' ? '' : units}}
            </td>
            <td class="text-right" v-else-if="header.value==='dutyPaidValue'">
              {{totals.totalValueDutyPaid | currency}}
            </td>
            <td v-else-if="!printing || header.print === undefined || header.print"/>
          </template>
        </tr>
      </template>

      <template v-slot:expanded-item="{ item, headers }">
        <td :colspan="headers.length">
          <v-card class="my-4">
            <v-tabs vertical icons-and-text v-model="expandTab">
              <v-tab><v-icon>mdi-information</v-icon><div class="text-caption">Info</div></v-tab>
              <v-tab><div><v-icon>mdi-tag</v-icon></div><div class="text-caption">In-bond</div></v-tab>
              <v-tab><div><v-icon>mdi-tag</v-icon></div><div class="text-caption">Duty-paid</div></v-tab>
              <v-tab v-if="item.description"><div><v-icon>mdi-cog</v-icon></div><div class="text-caption">Production</div></v-tab>
              <v-tabs-items :class="expansionPanelClass" v-model="expandTab">
                <v-tab-item :class="expansionPanelClass">
                  <v-container class="pa-4 text-caption" :class="expansionPanelClass">
                    <v-row dense>
                      <v-col cols="1">
                        <v-img v-if="item.pictureURL!=null" :src="item.pictureURL" width="100" height="120" contain class="ma-2"/>
                      </v-col>
                      <v-col cols="6" offset="1">
                        <v-container class="pa-0">
                          <v-row no-gutters>
                            <v-col cols="3" class="font-weight-medium text-right">Alcohol:</v-col><v-col cols="9" class="pl-2">{{item.mixedPack ? 'Mixed pack' : item.alcohol.name}}</v-col>
                          </v-row>
                          <v-row no-gutters>
                            <v-col cols="3" class="font-weight-medium text-right">Strength:</v-col><v-col cols="3" class="pl-2">{{item.strength | number(2)}}% alc./vol.</v-col>
                            <v-col cols="3" class="font-weight-medium text-right">Bottle size:</v-col><v-col cols="3" class="pl-2">{{item.bottleSize}} ml</v-col>
                            <v-col cols="3" class="font-weight-medium text-right">Container type:</v-col><v-col cols="3" class="pl-2">{{item.containerType}}</v-col>
                          </v-row>
                          <v-row no-gutters>
                            <v-col cols="3" class="font-weight-medium text-right">Case size:</v-col><v-col cols="3" class="pl-2">{{item.caseSize}} bottles</v-col>
                            <v-col cols="3" class="font-weight-medium text-right">Case weight:</v-col><v-col cols="3" class="pl-2">{{item.caseWeight | number(3)}} kg</v-col>
                          </v-row>
                          <v-row no-gutters>
                            <v-col cols="3" class="font-weight-medium text-right">Pallet:</v-col><v-col cols="3" class="pl-2">{{item.palletTI}} x {{item.palletHI}}</v-col>
                            <v-col cols="3" class="font-weight-medium text-right">Cases per pallet:</v-col><v-col cols="3" class="pl-2">{{item.palletHI*item.palletTI}}</v-col>
                          </v-row>
                          <v-row no-gutters>
                            <v-col cols="3" class="font-weight-medium text-right">UPC:</v-col><v-col cols="3" class="pl-2">{{item.upc}}</v-col>
                            <v-col cols="3" class="font-weight-medium text-right">SCC:</v-col><v-col cols="3" class="pl-2">{{item.scc}}</v-col>
                          </v-row>
                          <v-row no-gutters>
                            <v-col cols="3" class="font-weight-medium text-right">In-bond per bottle:</v-col><v-col cols="3" class="pl-2">{{item.inBondBottles ? item.inBondValue/item.inBondBottles : 0 | currency}}</v-col>
                            <v-col cols="3" class="font-weight-medium text-right">In-bond per case:</v-col><v-col cols="3" class="pl-2">{{item.inBondBottles ? item.inBondValue/item.inBondBottles*item.caseSize : 0 | currency}}</v-col>
                          </v-row>
                          <v-row no-gutters>
                            <v-col cols="3" class="font-weight-medium text-right">Duty-paid per bottle:</v-col><v-col cols="3" class="pl-2">{{item.dutyPaidBottles ? item.dutyPaidValue/item.dutyPaidBottles : 0 | currency}}</v-col>
                            <v-col cols="3" class="font-weight-medium text-right">Duty-paid per case:</v-col><v-col cols="3" class="pl-2">{{item.dutyPaidBottles ? item.dutyPaidValue/item.dutyPaidBottles*item.caseSize : 0 | currency}}</v-col>
                          </v-row>
                        </v-container>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-tab-item>
                <v-tab-item>
                  <v-simple-table dense class="pa-2" :class="expansionPanelClass" style="width:80%">
                    <thead>
                      <tr>
                        <th v-if="!filters.siteId && exciseSites.length>1" :class="expansionPanelClass" width="30%">Site</th>
                        <template v-if="company.settings.lotTrackingForProducts">
                          <th :class="expansionPanelClass" width="20%">Lot</th>
                          <th class="text-right" :class="expansionPanelClass" width="20%">Strength</th>
                        </template>
                        <th class="text-right" :class="expansionPanelClass" width="15%">In-bond on hand</th>
                        <th class="text-right" :class="expansionPanelClass" width="15%" v-if="values">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="(line,i) of productInventory(item)">
                        <tr v-if="line.inBondBottles!==0" :key="i">
                          <td v-if="!filters.siteId && exciseSites.length>1" class="text-caption">
                            {{exciseSites.find(site => site.id === line.siteId).name}}
                          </td>
                          <template>
                            <td class="text-caption">
                              <a @click="$router.push({name:'ReportsView',params:{back:[{text:'Products',to:{name:'InventoryProducts'}}]},query:{to:'reports/inventory/ReportsLot',id:item.id,lot:line.lot,forward:0}})">{{line.lot}}</a>
                            </td>
                            <td class="text-caption text-right">
                              {{line.strength | number(2)}}% alc./vol.
                            </td>
                          </template>
                          <td class="text-caption text-right">
                            {{convertToUnits(item,line.inBondBottles) | number(precision)}} {{displayUnits(item)}}
                          </td>
                          <td v-if="values" class="text-caption text-right">{{line.inBondValue | currency}}</td>
                        </tr>
                      </template>
                    </tbody>
                  </v-simple-table>
                </v-tab-item>
                <v-tab-item>
                  <v-simple-table dense class="pa-2" :class="expansionPanelClass" style="width:80%">
                    <thead>
                      <tr>
                        <th v-if="!filters.siteId && allSites.length>1" :class="expansionPanelClass" width="35%">Site</th>
                        <th :class="expansionPanelClass" width="20%" v-if="company.settings.lotTrackingForDutyPaid">Lot</th>
                        <th class="text-right" :class="expansionPanelClass" width="15%">Duty-paid on hand</th>
                        <th class="text-right" :class="expansionPanelClass" width="15%" v-if="values">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="(line,i) of productInventory(item)">
                        <tr v-if="line.dutyPaidBottles!==0" :key="i">
                          <td v-if="!filters.siteId && allSites.length>1" class="text-caption">
                            {{allSites.find(site => site.id === line.siteId).name}}
                          </td>
                          <td class="text-caption" v-if="company.settings.lotTrackingForDutyPaid">
                            <a @click="$router.push({name:'ReportsView',params:{back:[{text:'Products',to:{name:'InventoryProducts'}}]},query:{to:'reports/inventory/ReportsLot',id:item.id,lot:line.lot,forward:0}})">{{line.lot}}</a>
                          </td>
                          <td class="text-caption text-right">
                            {{convertToUnits(item,line.dutyPaidBottles) | number(precision)}} {{displayUnits(item)}}
                          </td>
                          <td v-if="values" class="text-caption text-right">{{line.dutyPaidValue | currency}}</td>
                        </tr>
                      </template>
                    </tbody>
                  </v-simple-table>
                </v-tab-item>
                <v-tab-item>
                  <div class="pa-4">{{ item.description }}</div>
                </v-tab-item>
              </v-tabs-items>
            </v-tabs>
          </v-card>
        </td>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="productToDelete.name" @delete="performDeleteProduct"/>
  </div>
</template>

<script>
import InventoryProductMenu from '@/views/inventory/products/InventoryProductMenu'
import InventoryProductsImport from '@/views/inventory/products/InventoryProductsImport'
import InventoryCategoriesSelect from '@/views/inventory/categories/InventoryCategoriesSelect'
import InventoryProductsBatchEdit from '@/views/inventory/products/InventoryProductsBatchEdit'
import {format} from 'date-fns'

export default {
  name: 'InventoryProducts',
  components: {InventoryProductMenu,InventoryProductsImport,InventoryCategoriesSelect,InventoryProductsBatchEdit},
  data() {
    return {
      expandTab: 0,
      units: localStorage.getItem('product.units') ?? 'bottles',
      inBondLowInventoryCount: 0,
      dutyPaidLowInventoryCount: 0,
      showStatus: false,
      searchDisplay: '',
      today: format(new Date(), 'yyyy-MM-dd'),
      filters: {
        categoryId: null,
        asof: format(new Date(), 'yyyy-MM-dd'),
        status: '1',
        mixedPack: '',
        strength: '0',
        inBondLowInventory: '0',
        dutyPaidLowInventory: '0',
        siteId: null,
        search: '',
        searchField: 'name',
        id: null,
        upc: ''
      },
      totals: {totalBottlesInBondOnHand:0,totalCasesInBondOnHand:0,totalUnitsInBondOnHand:0,totalPalletsInBondOnHand:0,totalLAAInBondOnHand:0,totalLitresInBondOnHand:0,
               totalBottlesDutyPaidOnHand:0,totaCasesDutyPaidOnHand:0,totaUnitsDutyPaidOnHand:0,totalPalletsDutyPaidOnHand:0,totalLAADutyPaidOnHand:0,totalLitresDutyPaidOnHand:0,
               totalBottlesInBondOnOrder:0,totalCasesInBondOnOrder:0,totalUnitsInBondOnOrder:0,totalPalletsInBondOnOrder:0,totalLAAInBondOnOrder:0,totalLitresInBondOnOrder:0,
               totalBottlesDutyPaidOnOrder:0,totaCasesDutyPaidOnOrder:0,totaUnitsDutyPaidOnOrder:0,totalPalletsDutyPaidOnOrder:0,totalLAADutyPaidOnOrder:0,totalLitresDutyPaidOnOrder:0,
               totalValueInBond:0,totalValueDutyPaid:0},
      categories: [],
      categoryName: '',
      productToDelete: {},
      showDelete: false,
      doImport: false,
      defaultColumns: [{ value: 'name', visible:true},
                       { value: 'sku', visible:true},
                       { value: 'category.name', visible:false},
                      {value:'inBondBottles', visible:true},
                      {value:'inBondOnOrder', visible:false},
                      {value:'inBondAvailable', visible:false},
                      {value:'inBondValue', visible:true},
                       { value: 'inBondPerCase', visible:false},
                       { value: 'inBondPerBottle', visible:false},
                       { value: 'defaultBondedPrice', visible:false},
                      {value:'dutyPaidBottles', visible:true},
                      {value:'dutyPaidOnOrder', visible:false},
                      {value:'dutyPaidAvailable', visible:false},
                      {value:'dutyPaidValue', visible:true},
                       { value: 'dutyPaidPerCase', visible:false},
                       { value: 'dutyPaidPerBottle', visible:false},
                       { value: 'defaultPrice', visible:false},
                       { value: 'alcohol.name', visible:false},
                       { value: 'upc', visible:false},
                       { value: 'scc', visible:false},
                       { value: 'caseSize', visible:false},
                       { value: 'bottleSize', visible:false},
                       { value: 'containerType', visible:false},
                       { value: 'strength', visible:false},
                       { value: 'duration', visible:false}],
      headers: [{ text: 'PRODUCT', value: 'name', width:'30%',sortable:true},
                { text: 'SKU', value: 'sku', width:'10%',sortable:true},
                { text: 'CATEGORY', value: 'category.name', width:'20%',sortable:true},
                { text: 'IN-BOND ON HAND', value: 'inBondBottles', width:'15%',sortable:false,align:'right'},
                { text: 'DUTY-PAID ON HAND', value: 'dutyPaidBottles', width:'15%',sortable:false,align:'right'},
                { text: 'IN-BOND ON ORDER', value: 'inBondOnOrder', width:'15%',sortable:false,align:'right'},
                { text: 'DUTY-PAID ON ORDER', value: 'dutyPaidOnOrder', width:'15%',sortable:false,align:'right'},
                { text: 'IN-BOND AVAILABLE', value: 'inBondAvailable', width:'15%',sortable:false,align:'right'},
                { text: 'DUTY-PAID AVAILABLE', value: 'dutyPaidAvailable', width:'15%',sortable:false,align:'right'},
                { text: 'IN-BOND VALUE', value: 'inBondValue', width:'15%',sortable:false,align:'right', omit:() => !this.values},
                { text: 'DUTY-PAID VALUE', value: 'dutyPaidValue', width:'15%',sortable:false,align:'right', omit:() => !this.values},
                { text: 'SPIRIT', value: 'alcohol.name', width:'30%',sortable:true},
                { text: 'UPC', value: 'upc', width:'10%',sortable:false},
                { text: 'SCC', value: 'scc', width:'10%',sortable:false},
                { text: 'CASE SIZE', value: 'caseSize', width:'10%',sortable:false,align:'right'},
                { text: 'BOTTLE SIZE', value: 'bottleSize', width:'10%',sortable:false,align:'right'},
                { text: 'CONTAINER', value: 'containerType', width:'10%',sortable:false},
                { text: 'STRENGTH', value: 'strength', width:'15%',sortable:true,align:'right'},
                { text: 'IN-BOND PRICE', value: 'defaultBondedPrice', width:'10%',sortable:false,align:'right'},
                { text: 'DUTY-PAID PRICE', value: 'defaultPrice', width:'10%',sortable:false,align:'right'},
                { text: 'TIME/CASE', value: 'duration', width:'10%',sortable:true},
                { text: 'IN-BOND $/CASE', value: 'inBondPerCase', width:'10%',sortable:false,align:'right', omit:() => !this.values},
                { text: 'IN-BOND $/BOTTLE', value: 'inBondPerBottle', width:'10%',sortable:false,align:'right', omit:() => !this.values},
                { text: 'DUTY-PAID $/CASE', value: 'dutyPaidPerCase', width:'10%',sortable:false,align:'right', omit:() => !this.values},
                { text: 'DUTY-PAID $/BOTTLE', value: 'dutyPaidPerBottle', width:'10%',sortable:false,align:'right', omit:() => !this.values}]
    }
  },
  computed: {
    precision() {
      return {bottles:0,cases:1,pallets:1,LAA:3,litres:3}[this.units];
    },
    allSites() {
      return this.$store.getters.sites(true);
    },
    exciseSites() {
      return this.$store.getters.sites(true, {B262:true});
    },
    siteName() {
      return this.allSites.find(site => site.id === this.filters.siteId)?.name;
    },
    company() {
      return this.$store.getters.company;
    },
    readOnly() {
      return !this.company.permissions.includes('inventory.write');
    },
    production() {
      return this.company.permissions.includes('production.write');
    },
    values() {
      return this.company.permissions.includes('values');
    },
    listFilters() {
      const filters = [];
      if (this.filters.id)
        filters.push({field:'id', comparator:'eq', value:this.filters.id});
      if (this.filters.upc)
        filters.push({field:'upc', comparator:'contains', value:this.filters.upc});
      if (this.filters.inBondLowInventory == '1')
        filters.push({field:'inBondLowInventory', comparator:'eq', value:'true'});
      if (this.filters.dutyPaidLowInventory == '1')
        filters.push({field:'dutyPaidLowInventory', comparator:'eq', value:'true'});
      if (this.filters.mixedPack != '')
        filters.push({field:'mixedPack', comparator:'eq', value:this.filters.mixedPack == '1' ? 'true' : 'false'});
      if (this.filters.status == '1' || this.filters.status == '0')
        filters.push({field:'active', comparator:'eq', value:this.filters.status == '1' ? 'true' : 'false'});
      if (this.filters.strength == '1')
        filters.push({field:'strength', comparator:'le', value:'7'});
      else if (this.filters.strength == '2')
        filters.push({field:'strength', comparator:'gt', value:'7'});
      if (this.filters.categoryId)
        filters.push({field:'categoryId', comparator:'in', value:this.filters.categoryId});
      if (this.filters.siteId)
        filters.push({field:'siteId', comparator:'eq', value:this.filters.siteId});
      if (this.filters.search)
        filters.push({field:this.filters.searchField, comparator:'contains', value:this.filters.search});

      return filters;
    },
    listParameters() {
      return this.filters.asof ? [{name:'asof', value:`${this.filters.asof} 23:59:59`}] : [];
    }
  },
  watch: {
    units() {
      localStorage.setItem('product.units', this.units);
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');

    const results = await this.$apiCall(`query InventoryProducts {
                                            inBondProductsLowInventory: productsLowInventory(bonded:true)
                                            dutyPaidProductsLowInventory: productsLowInventory(bonded:false)
                                          }`,
                                      {});
    if (!results.error) {
      this.inBondLowInventoryCount = results.data.inBondProductsLowInventory;
      this.dutyPaidLowInventoryCount = results.data.dutyPaidProductsLowInventory;
    }

    if (!['InventoryProductsEdit', 'InventoryProductsTransactions','InventoryProductCategories','InventoryProductsBatchMove','InventoryProductsReconcile'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        (this.inBondLowInventoryCount + this.dutyPaidLowInventoryCount) && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    displayUnits(product) {
      if (this.units === 'bottles') {
        if (product.mixedPack)
          return 'packs';
        if (product.containerType === 'Can')
          return 'cans'
      }
      return this.units;
    },
    scan({type, id, code}) {
      if (type === 'Product' || code)
        Object.assign(this.filters, {categoryId:null, siteId:null, status:'2', strength:'0', inBondLowInventory:'0', dutyPaidLowInventory:'0', asof:this.today, search:'', id:null, upc:''});
      if (type === 'Product')
        this.filters.id = id;
      else if (code)
        this.filters.upc = code;
    },
    listBuilt({list}) {
      if ((this.filters.id || this.filters.upc) && list.edges.length === 1)
        this.$refs.table.expanded.push(list.edges[0].node);
    },
    productInventory(product) {
      if (!this.filters.siteId)
        return product.sites;
      const result = product.sites.reduce((result, site) => {
        if ((!this.filters.siteId || site.siteId === this.filters.siteId) && (site.inBondBottles || site.inBondValue)) {    // Site is okay
          if (result[site.lot]) {
            result[site.lot].inBondBottles += site.inBondBottles;
            result[site.lot].inBondValue += site.inBondValue;
          }
          else
            result[site.lot] = {...site};
        }
        return result;
      }, {});
      return Object.values(result);
    },
    convertToUnits(product, bottles) {
      if (this.units === 'bottles')
        return bottles;
      if (this.units === 'units')
        return this.$roundNumber(bottles / product.bottlesPerUnit, 1);
      if (this.units === 'cases')
        return this.$roundNumber(bottles / product.caseSize, 1);
      if (this.units === 'pallets')
        return this.$roundNumber(bottles / product.caseSize / (product.palletTI*product.palletHI), 1);
      if (this.units === 'LAA')
        return this.$roundNumber(bottles * product.bottleSize / 1000 * product.strength / 100, 3);
      if (this.units === 'litres')
        return this.$roundNumber(bottles * product.bottleSize / 1000, 3);
    },
    async performDeleteProduct() {
      this.loading = true;
      const results = await this.$apiCall(`mutation InventoryProducts($id: ID!) {
                                             objectDelete(id:$id)
                                            }`,
                                          {id:this.productToDelete.id});
      this.loading = false;
      if (!results.error) {
        this.showDelete = false;
        this.$store.commit('productDelete', this.productToDelete.id);
        this.$refs.table?.buildList();
        this.$store.commit('setError', {type:'success',message:`Deleted product ${this.productToDelete.name}`});
      }
    },
    async sync() {
      this.loading = true;
      const results = await this.$apiCall(`mutation InventoryProducts {
                                             accountingIntegrationSync(step:"products") {name}
                                           }`, {});
      this.$refs.table?.buildList();
      if (!results.error)
        this.$store.commit('setError', {type:'success',message:`Synced products with QBO`});
    }
  }
}
</script>